import { NumberFieldModel } from "@/common/commonForms";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "@/i18n/tt";
import { toDate } from "@/utils/Dates";
import orderBy from "lodash/orderBy";
import { ReactNode } from "react";
import { TableColumn } from "./DataTable";

export type SortDirection = "asc" | "desc";

export type DataTableSort = { by: string; direction: SortDirection };

export const isCellReadonly = <T>(
  col: TableColumn<T>,
  row: T,
  rowIndex: number,
): boolean | undefined =>
  typeof col.isReadonly === "boolean" ? col.isReadonly : col.isReadonly?.(row, rowIndex);

const defaultSortValue = <T>(
  col: TableColumn<T>,
  row: T,
  i: number,
  i18n: PreppedI18nState,
): ReactNode | TMessage | TMessageParam | string | number | undefined => {
  const val = col.getValue?.(row, i, i18n);
  switch (true) {
    case Array.isArray(val):
      switch (val[0]) {
        case "select":
          return val[1].labelFn(val[1].value, i18n);
        case "mselect":
          return (val[1].labelFn ?? val[1].model.getLabel)?.(val[1].model.value, i18n);
        case "mdate-input":
          return val[1].model.validatedValue;
        case "mtext-input":
          if (val[1].model instanceof NumberFieldModel) {
            return val[1].model.toNumberOrZero();
          }
          return val[1].model.inputValue;
        default:
          return undefined;
      }
    case isTMessage(val):
      return i18n.t(val);
    case isTMessageParam(val):
      switch (val.type) {
        case "amount":
        case "number":
        case "exactAmount":
        case "nominal-value":
        case "percent":
        case "price":
          return val.value;
        case "date":
        case "datetime":
        case "year":
        case "year-month":
          return val.value ? toDate(val.value).getTime() : undefined;
        default:
          return i18n.tf(val);
      }
    default:
      return val;
  }
};

const sortIteratee = <T>(columns: TableColumn<T>[], sortedBy: string, i18n: PreppedI18nState) => {
  const col = columns.find((c) => c.name === sortedBy);
  return col
    ? (row: T, index: number) => {
        const s = col.getSortValue
          ? col.getSortValue(row, index, i18n)
          : defaultSortValue(col, row, index, i18n);
        if (typeof s === "string") {
          return s.toLocaleLowerCase();
        } else if (isTMessage(s) || isTMessageParam(s)) {
          return i18n.tf(s).toLocaleLowerCase();
        }
        return s;
      }
    : sortedBy;
};

export const sortTableData = <T>(
  columns: TableColumn<T>[],
  data: T[],
  sort: DataTableSort | null | undefined,
  i18n: PreppedI18nState,
): T[] =>
  sort?.by ? orderBy(data, [sortIteratee(columns, sort.by, i18n)], [sort.direction]) : data;
